import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./AegisPaywall.module.scss"

/**
 * Aegis — a NON-ONBOARDING paywall (paywall type rolled at random this run —
 * rolled *Non-onboarding*: an in-app upgrade a user hits while browsing, not a
 * first-run flow). Built to the active Slack standing directive ("build
 * paywalls centered around loss aversion, use fear as the driving factor.
 * Include some amazing animations, highlight our feature list. Target new
 * users"): the fear is the oldest one in photography — the single point of
 * failure. A memory that lives in exactly ONE place is one bad day for one
 * phone away from gone forever.
 *
 * Mechanic — RAISE THE AEGIS. One memory floats alone in the dark, cold and
 * desaturated, tagged "only copy". Real-world hazards ("lost phone", "cracked
 * screen", "stolen", "prying eyes") orbit inward around it — the honest source
 * of the fear, never the app. The interaction is not a tap: you PRESS AND HOLD
 * to raise its shield. A ring of light sweeps around the memory as you hold;
 * the orbiting hazards are pushed outward and dissolve; the memory warms back
 * to full colour; copies of it fan out into a protected lattice (a backup keeps
 * safe *copies*); the four Premium protections ignite one after another; and a
 * gold lock seals the whole set — "Protected · safe copies kept". Holding to
 * commit *is* the CTA: complete the hold and the purchase begins.
 *
 * Honest loss aversion. The hazards are the real world — a lost, broken or
 * stolen phone, or someone else's eyes — never the app deleting anything.
 * Explicit copy: your photos are always yours and are never deleted; protection
 * just keeps encrypted, private copies so a bad day for one phone is never a
 * bad day for a memory. No countdown, no fake scarcity, honest savings from the
 * real prices.
 *
 * Divergence from the set: the signature is a *press-and-hold guardianship
 * gesture* driving a single-point-of-failure → multiplied-safe-copies transform
 * — not Brink's downward gravity + light-beam catch, Artifact's glitch-restore,
 * Lifeline's braided cord, or SafetyNet's net. Interaction model is hold, not
 * tap; value frame is one-copy fragility → many safe copies; register is calm
 * guardianship / resolve, cold slate → warm gold, not vertigo. Yearly is the
 * featured, most-prominent price; monthly is one demoted line. Self-contained
 * on the built-in debug product data + bundled photos; no native injection.
 */

// Real memories from the bundled set. `HERO` is the exposed "only copy"; the
// rest fan out into the protected lattice as the shield rises — your library,
// backed up.
const HERO = "mem7.jpg"
const LATTICE = [
  "mem2.jpg",
  "mem11.jpg",
  "mem4.jpg",
  "mem14.jpg",
  "mem9.jpg",
  "mem1.jpg",
]

// Positions for the six lattice tiles, in stage-relative percentages, ringed
// around the hero. They settle in as the shield charges (staggered by `at`).
const LATTICE_POS = [
  { x: 12, y: 16, r: -9, at: 0.12 },
  { x: 78, y: 12, r: 8, at: 0.28 },
  { x: 6, y: 60, r: 7, at: 0.44 },
  { x: 84, y: 58, r: -8, at: 0.58 },
  { x: 26, y: 84, r: 5, at: 0.72 },
  { x: 66, y: 86, r: -6, at: 0.84 },
]

// Real-world losses that orbit the exposed memory — the honest fear, flung out
// and dissolved as protection rises. Never the app.
const HAZARDS = [
  "lost phone",
  "cracked screen",
  "stolen",
  "prying eyes",
  "water",
]

// The protections raised with the shield — real Premium capabilities, lit one
// after another as the hold completes.
interface Guard {
  label: string
  detail: string
  icon: IconName
  at: number
}
const GUARDS: Guard[] = [
  {
    label: "Encrypted private backup",
    detail: "Safe copies, kept off your phone",
    icon: "cloud",
    at: 0.2,
  },
  {
    label: "PIN-locked albums",
    detail: "Only you get past the lock",
    icon: "lock",
    at: 0.42,
  },
  {
    label: "Original quality",
    detail: "Full resolution, never shrunk",
    icon: "image",
    at: 0.64,
  },
  {
    label: "On every device",
    detail: "Reach them anywhere you sign in",
    icon: "devices",
    at: 0.84,
  },
]

type IconName =
  | "lock"
  | "cloud"
  | "image"
  | "devices"
  | "shield"
  | "check"
  | "close"
  | "arrow"
  | "spark"

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

const Icon = ({ name }: { name: IconName }) => {
  switch (name) {
    case "lock":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="4.5"
            y="10.4"
            width="15"
            height="10.2"
            rx="3"
            fill="currentColor"
          />
          <path
            d="M8 10.4V7.6a4 4 0 0 1 8 0v2.8"
            stroke="currentColor"
            strokeWidth="2.1"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      )
    case "cloud":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M7 18h10a4 4 0 0 0 .5-7.97A5.5 5.5 0 0 0 6.5 9.2 3.9 3.9 0 0 0 7 18Z"
            fill="currentColor"
          />
          <path
            d="M12 15.4v-4.8m0 0 1.9 1.9m-1.9-1.9-1.9 1.9"
            stroke="var(--ag-ink)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "image":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="3.5"
            y="5"
            width="17"
            height="14"
            rx="3"
            fill="currentColor"
          />
          <circle cx="9" cy="10" r="1.7" fill="var(--ag-ink)" />
          <path
            d="m5 17 4-4 3.2 3 2.3-2.3L19 17.5"
            stroke="var(--ag-ink)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "devices":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect
            x="2.5"
            y="5.5"
            width="13"
            height="10"
            rx="2"
            fill="currentColor"
          />
          <rect
            x="16"
            y="8.5"
            width="5.5"
            height="10"
            rx="1.6"
            fill="currentColor"
          />
          <path
            d="M6 19h6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
    case "shield":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3.2 5 6v5.4c0 4.2 2.9 7.2 7 9.2 4.1-2 7-5 7-9.2V6l-7-2.8Z"
            fill="currentColor"
          />
        </svg>
      )
    case "check":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m5 12.5 4.5 4.5L19 7"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "close":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="m6 6 12 12M18 6 6 18"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      )
    case "arrow":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M5 12h13m0 0-5-5m5 5-5 5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )
    case "spark":
      return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3v4m0 10v4m9-9h-4M7 12H3m13.5-6.5-2.8 2.8M10.3 13.7l-2.8 2.8m11-.1-2.8-2.8M10.3 10.3 7.5 7.5"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
          />
        </svg>
      )
  }
}

const RING_C = 2 * Math.PI * 132 // circumference of the shield progress ring

const AegisPaywall = () => {
  const { state, actions } = useAppState()

  const premium = state.productData?.premium
  const locale = premium?.yearlyProduct?.priceLocale

  const money = (v: number | undefined, fallback: string) =>
    v != null && locale ? formatCurrency(v, locale) : fallback

  const plan = useMemo(
    () => ({
      yearly: premium?.yearlyProduct?.price ?? 35.99,
      monthly: premium?.monthlyProduct?.price ?? 13.99,
      yearlyProduct: premium?.yearlyProduct,
      monthlyProduct: premium?.monthlyProduct,
    }),
    [premium],
  )

  const [billing, setBilling] = useState<"yearly" | "monthly">("yearly")

  // Two scalars fully describe the scene:
  //   charge 0 → memory exposed, only copy … 1 → shield raised, sealed
  //   clock  → continuous time for the orbit / flicker
  const [charge, setCharge] = useState(0)
  const chargeRef = useRef(0)
  const [clock, setClock] = useState(0)
  const clockRef = useRef(0)

  const holdingRef = useRef(false) // pointer is pressed
  const forceRef = useRef(false) // tap / keyboard auto-complete in flight
  const pointerUsedRef = useRef(false) // a pointer sequence is driving the hold
  const sealedRef = useRef(false)
  const [sealed, setSealed] = useState(false)
  const rafDrive = useRef(0)
  const rafClock = useRef(0)
  const lastT = useRef(0)
  const reduced = useRef(false)

  const CHARGE_MS = 1150
  const DRAIN_MS = 620

  const selectedProduct =
    billing === "yearly" ? plan.yearlyProduct : plan.monthlyProduct
  const selectedRef = useRef(selectedProduct)
  selectedRef.current = selectedProduct

  const seal = useCallback(() => {
    if (sealedRef.current) return
    sealedRef.current = true
    setSealed(true)
    if (selectedRef.current) native.subscribe(selectedRef.current)
  }, [])

  // Continuous clock — drives the slow orbit and the exposed flicker.
  useEffect(() => {
    reduced.current =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
    if (reduced.current) return
    const start = performance.now()
    const tick = (now: number) => {
      clockRef.current = (now - start) / 1000
      setClock(clockRef.current)
      rafClock.current = requestAnimationFrame(tick)
    }
    rafClock.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafClock.current)
  }, [])

  // Tint the OS top inset to the scene's dark ground; run edge to edge.
  useEffect(() => {
    actions.setHeaderInsetColor("#070a14")
    actions.setIgnoreFooterInset(true)
  }, [actions])

  // The drive loop: while held (or forced), the shield charges; when released
  // before full, it eases back down. Reaching 1 latches "sealed".
  const drive = useCallback(
    (now: number) => {
      const dt = lastT.current ? now - lastT.current : 16
      lastT.current = now
      const up = holdingRef.current || forceRef.current
      const rate = up ? dt / CHARGE_MS : -dt / DRAIN_MS
      chargeRef.current = clamp01(chargeRef.current + rate)
      setCharge(chargeRef.current)
      if (chargeRef.current >= 1) {
        seal()
        return
      }
      if (chargeRef.current <= 0 && !up) {
        rafDrive.current = 0
        lastT.current = 0
        return
      }
      rafDrive.current = requestAnimationFrame(drive)
    },
    [seal],
  )

  const kick = useCallback(() => {
    if (rafDrive.current) return
    lastT.current = 0
    rafDrive.current = requestAnimationFrame(drive)
  }, [drive])

  const startHold = useCallback(() => {
    if (sealedRef.current) return
    pointerUsedRef.current = true
    if (reduced.current) {
      chargeRef.current = 1
      setCharge(1)
      seal()
      return
    }
    holdingRef.current = true
    kick()
  }, [kick, seal])

  const endHold = useCallback(() => {
    holdingRef.current = false
    kick()
  }, [kick])

  // Quick tap / keyboard activation → auto-complete the hold so the gesture is
  // never a dead end for tappers or assistive tech.
  const autoComplete = useCallback(() => {
    // Skip the trailing click that follows a real pointer hold-and-release;
    // only genuine taps / keyboard / programmatic clicks complete this way.
    if (pointerUsedRef.current) {
      pointerUsedRef.current = false
      return
    }
    if (sealedRef.current || holdingRef.current) return
    if (reduced.current) {
      chargeRef.current = 1
      setCharge(1)
      seal()
      return
    }
    forceRef.current = true
    kick()
  }, [kick, seal])

  // Auto-demo on load: raise the shield partway so the interaction reads, then
  // let it ease back — a living hint to "hold to protect".
  useEffect(() => {
    if (reduced.current) return
    const t1 = setTimeout(() => {
      if (sealedRef.current || holdingRef.current) return
      forceRef.current = true
      kick()
    }, 780)
    // Always release the demo — never let the preview charge all the way to a
    // self-seal on load. The user's own hold is what completes it.
    const t2 = setTimeout(() => {
      forceRef.current = false
      kick()
    }, 1340)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
      cancelAnimationFrame(rafDrive.current)
    }
  }, [kick])

  const yearlyPrice = money(plan.yearly, "$35.99")
  const perMonthOfYear = money(plan.yearly / 12, "$2.99")
  const monthlyPrice = money(plan.monthly, "$13.99")
  const savePercent = Math.max(
    0,
    Math.round((1 - plan.yearly / (plan.monthly * 12)) * 100),
  )

  const close = useCallback(() => native.postNativeEvent("close"), [])

  // ---- Derived visuals -----------------------------------------------------
  const c = charge
  const ec = easeOutCubic(c)
  const exposure = 1 - c
  const flicker = reduced.current ? 0 : Math.sin(clock * 6.2) * 0.045 * exposure
  const heroSat = lerp(0.24, 1.12, c)
  const heroBright = lerp(0.7, 1.02, c) + flicker
  const ringOffset = RING_C * (1 - c)
  const armed = sealed || c > 0.995

  // Orbiting hazards: flung outward and faded as the shield rises.
  const hazardNodes = HAZARDS.map((text, i) => {
    const ang = (i / HAZARDS.length) * Math.PI * 2 + clock * 0.3 - Math.PI / 2
    const radius = lerp(150, 258, ec)
    const breathe = reduced.current ? 0 : Math.sin(clock * 1.6 + i) * 4
    const x = Math.cos(ang) * (radius + breathe)
    const y = Math.sin(ang) * (radius + breathe) * 0.66
    const opacity = Math.pow(exposure, 1.5) * 0.92
    return { text, x, y, opacity }
  })

  return (
    <div
      className={styles.page}
      data-armed={armed}
      style={
        {
          "--charge": c,
          "--exposure": exposure,
          "--seal": ec,
        } as React.CSSProperties
      }
    >
      <div className={styles.aura} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Icon name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Icon name="shield" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          This memory lives in
          <br />
          <span className={styles.hl}>exactly one place.</span>
        </h1>
        <p className={styles.sub}>
          One phone. One copy. No safety net. Raise its shield and keep private,
          encrypted copies — so a lost, broken or stolen phone never means a
          lost memory.
        </p>
      </header>

      {/* Signature stage: the exposed memory, its orbiting hazards, the shield
          ring, and the protected lattice it fans out into. Press and hold
          anywhere on it to raise the shield. */}
      <section
        className={styles.stage}
        onPointerDown={startHold}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        aria-label="Press and hold to protect this memory"
      >
        <span className={styles.statusChip} data-armed={armed}>
          <span className={styles.statusDot} aria-hidden="true" />
          {armed ? "Protected · safe copies kept" : "Only copy · unprotected"}
        </span>

        {/* the protected lattice — your library, settling in behind the hero */}
        <div className={styles.lattice} aria-hidden="true">
          {LATTICE.map((f, i) => {
            const p = LATTICE_POS[i]
            const tp = clamp01((c - p.at) / 0.22)
            const etp = easeOutCubic(tp)
            return (
              <span
                className={styles.latticeTile}
                key={f}
                style={{
                  left: `${p.x}%`,
                  top: `${p.y}%`,
                  opacity: etp * 0.9,
                  transform: `translate(-50%, -50%) scale(${lerp(0.4, 1, etp)}) rotate(${lerp(p.r * 2.2, p.r, etp)}deg)`,
                }}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/nimbus/${f}`}
                  alt=""
                  draggable={false}
                  loading="eager"
                />
              </span>
            )
          })}
        </div>

        {/* orbiting real-world hazards */}
        <div className={styles.orbit} aria-hidden="true">
          {hazardNodes.map((h) => (
            <span
              className={styles.hazard}
              key={h.text}
              style={{
                transform: `translate(-50%, -50%) translate(${h.x}px, ${h.y}px)`,
                opacity: h.opacity,
              }}
            >
              {h.text}
            </span>
          ))}
        </div>

        {/* the shield ring sweeping around the memory as it charges */}
        <svg className={styles.ring} viewBox="0 0 300 300" aria-hidden="true">
          <defs>
            <linearGradient id="agRing" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#4d7cff" />
              <stop offset="0.6" stopColor="#8fb0ff" />
              <stop offset="1" stopColor="#ffc866" />
            </linearGradient>
          </defs>
          <circle className={styles.ringTrack} cx="150" cy="150" r="132" />
          <circle
            className={styles.ringFill}
            cx="150"
            cy="150"
            r="132"
            style={{ strokeDasharray: RING_C, strokeDashoffset: ringOffset }}
          />
        </svg>

        {/* the exposed memory — the only copy */}
        <div className={styles.heroWrap}>
          <span className={styles.ghost} data-g="1" aria-hidden="true">
            <img
              src={`${process.env.PUBLIC_URL}/nimbus/${HERO}`}
              alt=""
              draggable={false}
            />
          </span>
          <span className={styles.ghost} data-g="2" aria-hidden="true">
            <img
              src={`${process.env.PUBLIC_URL}/nimbus/${HERO}`}
              alt=""
              draggable={false}
            />
          </span>
          <span
            className={styles.hero}
            style={{ filter: `saturate(${heroSat}) brightness(${heroBright})` }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/nimbus/${HERO}`}
              alt=""
              draggable={false}
              loading="eager"
            />
            <span className={styles.cold} aria-hidden="true" />
            <span className={styles.heroTag} data-armed={armed}>
              {armed ? "backed up" : "only copy"}
            </span>
            <span
              className={styles.lockSeal}
              data-armed={armed}
              aria-hidden="true"
            >
              <Icon name="lock" />
            </span>
          </span>
        </div>
      </section>

      {/* the protections raised with the shield */}
      <ul className={styles.guards}>
        {GUARDS.map((g, i) => {
          const on = c >= g.at
          return (
            <li
              className={styles.guard}
              key={g.label}
              data-on={on}
              style={{ transitionDelay: `${i * 40}ms` }}
            >
              <span className={styles.guardIcon}>
                <Icon name={g.icon} />
              </span>
              <span className={styles.guardText}>
                <strong>{g.label}</strong>
                <span>{g.detail}</span>
              </span>
              <span className={styles.guardMark} aria-hidden="true">
                <Icon name={on ? "check" : "spark"} />
              </span>
            </li>
          )
        })}
      </ul>

      {/* reassurance — the App-Store-compliance spine, said plainly */}
      <p className={styles.reassure}>
        <Icon name="shield" />
        Your photos are always yours and are never deleted. Protection just
        keeps private, encrypted copies, so a bad day for one phone is never a
        bad day for a memory.
      </p>

      {/* pricing — yearly featured; monthly demoted to a single line */}
      <section className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${billing === "yearly" ? styles.yearOn : ""}`}
          onClick={() => setBilling("yearly")}
          aria-pressed={billing === "yearly"}
        >
          <span className={styles.yearBadge}>
            BEST VALUE · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>Premium · Yearly</span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>{perMonthOfYear}/mo, billed yearly</span>
            <span>Protection stays on all year</span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${billing === "monthly" ? styles.monthOn : ""}`}
          onClick={() => setBilling("monthly")}
          aria-pressed={billing === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        {/* the hold-to-commit CTA — the gesture IS the purchase */}
        <button
          className={styles.cta}
          data-armed={armed}
          onPointerDown={startHold}
          onPointerUp={endHold}
          onPointerLeave={endHold}
          onPointerCancel={endHold}
          onClick={autoComplete}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") autoComplete()
          }}
          aria-label={
            armed
              ? "Protected — start Premium yearly"
              : "Press and hold to protect your memories"
          }
        >
          <span
            className={styles.ctaFill}
            style={{ width: `${c * 100}%` }}
            aria-hidden="true"
          />
          <span className={styles.ctaBody}>
            <Icon name={armed ? "check" : "shield"} />
            <span className={styles.ctaLabel}>
              <strong>
                {armed
                  ? "Protected — starting Premium"
                  : "Hold to raise the shield"}
              </strong>
              {!armed && (
                <em>
                  press &amp; hold to protect ·{" "}
                  {billing === "yearly"
                    ? `${yearlyPrice}/yr`
                    : `${monthlyPrice}/mo`}
                </em>
              )}
            </span>
          </span>
        </button>

        <p className={styles.fine}>
          {billing === "yearly"
            ? `${yearlyPrice} per year (${perMonthOfYear}/mo)`
            : `${monthlyPrice} per month`}
          . Auto-renews until canceled · cancel anytime.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default AegisPaywall
